// httpClient.ts
// -------------
// One place to do four things every HTTP call to the condo backend needs:
//
//   1. Attach the Bearer access token from localStorage.
//   2. Enforce a 10s timeout (default fetch has no timeout — a hung backend
//      wedges the UI for ~120s).
//   3. Generate and propagate an X-Request-Id so the same id appears in
//      backend logs and in the user-visible error toast (Sprint 4 wires the
//      backend side; the header goes out today).
//   4. On a 401, attempt a SINGLE silent refresh and retry the original
//      request once. If the refresh itself 401s, hard-logout the user.
//
// Why "single-flight" the refresh: with N tabs / parallel requests, a burst
// of 401s could otherwise call /refresh-token N times, race each other, and
// rotate the family into reuse-detection territory (whichever request got
// the second presentation would trigger family revocation). The
// `inFlightRefresh` promise serialises every concurrent refresh into one.
//
// Sprint 110 (110.1) — the access token is held IN MEMORY ONLY (a module
// closure variable), never in localStorage/sessionStorage. Rationale: an
// access token in localStorage is readable by any XSS payload, turning a
// script-injection bug into full bearer-token theft. Keeping it in a JS
// closure means it lives only for the page's lifetime and is gone on reload —
// at which point `bootstrapAuth()` (below) silently re-mints one from the
// HttpOnly refresh cookie. The refresh token itself is NEVER stored client-side;
// it rides exclusively as the HttpOnly `condo_rt` cookie the backend sets.
//
// Migration note: prior code wrote the access token to `localStorage.authToken`
// (and the AuthContext comments already *claimed* it was in-memory — the code
// had drifted). This change makes the implementation match the documented
// intent and removes the XSS theft surface.

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8085/api';

const REQUEST_TIMEOUT_MS = 10_000;

// ── In-memory token store ───────────────────────────────────────────────────
//
// Module-scoped: shared by every importer of this module within the page, lost
// on full reload (by design). NOT persisted anywhere.
let accessTokenInMemory: string | null = null;

// Round-8 OBS-1: older builds (and the parallel "unified" auth context) used to
// persist a real JWT under these keys. Until the user explicitly logs out,
// clearTokens() never runs, so a stale (often higher-privilege) token could
// linger readable in localStorage indefinitely. Purge them on module load and
// on every clear, so an upgraded browser profile can't keep a token on disk.
const LEGACY_TOKEN_KEYS = [
  'authToken',
  'refreshToken',
  'unified_auth_condo_token',
  'unified_auth_medTech_token',
  'unified_auth_tenant_token',
];

function purgeLegacyTokens(): void {
  if (typeof window === 'undefined') return;
  try {
    LEGACY_TOKEN_KEYS.forEach(k => window.localStorage.removeItem(k));
  } catch { /* best effort */ }
}

// Run once when the module is first imported (app boot).
purgeLegacyTokens();

export function getAccessToken(): string | null {
  return accessTokenInMemory;
}

/**
 * The refresh token is the HttpOnly `condo_rt` cookie — it is never readable by
 * JS and never stored here. Retained for API back-compat; always returns null.
 */
export function getRefreshToken(): string | null {
  return null;
}

export function setTokens(access: string | null, _refresh: string | null): void {
  // Only the access token is tracked, and only in memory. The `refresh`
  // argument is intentionally ignored: the backend manages the refresh token
  // as an HttpOnly cookie; the SPA must never hold it.
  accessTokenInMemory = access ?? null;
}

export function clearTokens(): void {
  accessTokenInMemory = null;
  // Defensive: purge any legacy access token a prior build may have left in
  // localStorage, so an upgrade doesn't leave a readable token behind.
  purgeLegacyTokens();
}

// ── Bootstrap re-hydration (Sprint 110 — 110.1) ─────────────────────────────
//
// On a fresh page load the in-memory access token is empty. If the user still
// has a valid HttpOnly refresh cookie, this re-mints an access token silently
// so the session survives reload without ever persisting a token to disk.
//
// Returns the new access token (and stores it in memory), or null if there is
// no valid refresh cookie (genuinely logged out). It reuses the same
// single-flight `refreshAccessToken()` used by the 401-retry path, so a
// bootstrap that races with an in-flight request shares one network call.
let bootstrapPromise: Promise<string | null> | null = null;

export async function bootstrapAuth(): Promise<string | null> {
  // Already have a token this page-life → nothing to do.
  if (accessTokenInMemory) return accessTokenInMemory;
  // De-dupe concurrent bootstraps (e.g. ProtectedRoute + AuthContext both fire
  // on mount).
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = refreshAccessToken().finally(() => {
    // Allow a later bootstrap (after an explicit logout, say) to try again.
    setTimeout(() => { bootstrapPromise = null; }, 0);
  });
  return bootstrapPromise;
}

// ── X-Request-Id ────────────────────────────────────────────────────────────

function newRequestId(): string {
  // crypto.randomUUID is available in every browser we ship to and in jsdom.
  // Fallback keeps tests / very-old-browsers running.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'req-' + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ── CSRF (Sprint 6b) ───────────────────────────────────────────────────────
//
// Spring's CookieCsrfTokenRepository writes a non-HttpOnly XSRF-TOKEN cookie
// on every request. The SPA reads it and echoes the value in X-XSRF-TOKEN
// on state-changing requests. A cross-origin attacker can carry the cookie
// via the browser, but cannot read it (Same-Origin Policy on document.cookie),
// so they cannot forge a matching header.
//
// Round-10 fix: the backend's CSRF matcher (SecurityConfig, Sprint 110.5) keys
// off the AUTH METHOD, not a fixed path list — it requires CSRF on ANY mutating
// request that carries the `condo_rt` refresh cookie. Because apiFetch always
// sends `credentials: 'include'`, that cookie rides along with every
// authenticated mutation, so the backend demands an X-XSRF-TOKEN header on all
// of them — not just refresh-token/logout. The old whitelist only covered those
// two, so any OTHER mutating call (e.g. PUT /auth/user/{id} — the owner
// self-profile edit) got a 403. We now attach the header on every
// state-changing request when a token is readable. The header is harmless on a
// bearer-only request the server happens to exempt, and required on every one
// it protects.

const CSRF_HEADER_NAME = 'X-XSRF-TOKEN';

function readCsrfToken(): string | null {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);
  if (!match) return null;
  // Spring URL-encodes the value when needed; decode for the header.
  try { return decodeURIComponent(match[1]); }
  catch { return match[1]; }
}

/**
 * True when a request with this HTTP method needs the CSRF header. The backend
 * protects every state-changing method (POST/PUT/PATCH/DELETE) that arrives
 * with the refresh cookie; safe methods (GET/HEAD/OPTIONS) are never protected.
 */
function methodNeedsCsrf(method?: string): boolean {
  const m = (method ?? 'GET').toUpperCase();
  return m === 'POST' || m === 'PUT' || m === 'PATCH' || m === 'DELETE';
}

// ── Single-flight refresh ───────────────────────────────────────────────────

let inFlightRefresh: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (inFlightRefresh) return inFlightRefresh;

  // Sprint 6c — cookie is the only source of refresh tokens. The HttpOnly
  // cookie travels via `credentials: 'include'`; we don't need a body.
  // If the cookie is missing or the backend rejects it, we get a 401 and
  // hardLogout fires.
  inFlightRefresh = (async () => {
    try {
      const csrf = readCsrfToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      // Sprint 6b — CSRF token for the cookie-authed refresh endpoint.
      // Without a matching X-XSRF-TOKEN, the backend returns 403.
      if (csrf) headers[CSRF_HEADER_NAME] = csrf;
      const res = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers,
        // Empty body — the cookie carries the secret.
        body: '{}',
        credentials: 'include',
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      });
      if (!res.ok) return null;
      const body = await res.json();
      // Backend returns { accessToken, refreshToken: null, tokenType }
      // — the new refresh rode back as a Set-Cookie header.
      if (body && body.accessToken) {
        setTokens(body.accessToken, null);
        return body.accessToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      // Clear regardless of outcome so a future failure can refresh again.
      // Do this asynchronously so other awaiters of this promise see the
      // resolved value.
      setTimeout(() => { inFlightRefresh = null; }, 0);
    }
  })();

  return inFlightRefresh;
}

// ── Logout fallback ─────────────────────────────────────────────────────────

/**
 * Called when the refresh-token itself fails (or is missing). Clears local
 * auth state and bounces the user to the login page. We use
 * window.location.assign instead of react-router so we KILL the in-memory
 * Zustand store as a side effect — it's cheaper than auditing every
 * subscriber to make sure it tolerates a half-cleared user.
 */
function hardLogout(): void {
  clearTokens();
  if (typeof window !== 'undefined') {
    try {
      window.localStorage.removeItem('auth-storage');
      window.localStorage.removeItem('unified_auth_current_app');
    } catch { /* swallow — best effort */ }
    // Avoid redirect loops if we're already on the login page.
    if (!window.location.pathname.startsWith('/login-condo-app')) {
      window.location.assign(`/login-condo-app/${nextParamFor(window.location.pathname)}`);
    }
  }
}

/**
 * Sprint 137 (ADR-132) — the deep link the visitor asked for, carried in the
 * URL so it survives the bounce above.
 *
 * The trap this works around: ProtectedRoute's SPA `<Navigate state={{from}}>`
 * parks the intended page in `history.state`, but hardLogout's full page load
 * (deliberate — see above) throws the whole history entry away. So on the
 * expired-session path — a bookmark clicked the next morning, the *common*
 * real case — Login saw no `state.from` and dumped the user on the role
 * default. The query string is the only channel that survives a document load.
 *
 * Bare `/condo-app/` is deliberately NOT carried: it names no page, and Login
 * already falls back to it. Login re-validates the prefix on the way back in —
 * a query param is attacker-writable, so it is never trusted for being ours.
 */
function nextParamFor(pathname: string): string {
  const CONDO_PREFIX = '/condo-app/';
  if (!pathname.startsWith(CONDO_PREFIX) || pathname.length <= CONDO_PREFIX.length) return '';
  return `?next=${encodeURIComponent(pathname)}`;
}

// ── Core fetch wrapper ─────────────────────────────────────────────────────

export interface ApiFetchOptions extends RequestInit {
  /** When true, do not attach the Authorization header. Used by login + refresh. */
  skipAuth?: boolean;
  /** Internal: prevent infinite refresh loops. */
  _retried?: boolean;
}

export async function apiFetch(
  endpoint: string,
  options: ApiFetchOptions = {},
): Promise<Response> {
  const { skipAuth, _retried, headers, ...rest } = options;

  const token = skipAuth ? null : getAccessToken();
  const requestId = newRequestId();

  // For FormData bodies (multipart uploads) the browser MUST set
  // Content-Type itself so the boundary parameter lands correctly. If we
  // hard-code 'application/json' here, the upload arrives at the backend
  // with a missing boundary and is silently rejected as "no parts".
  // Detection is type-based (no string matching on the body) so any body
  // that's a FormData instance opts into browser-managed Content-Type.
  const isMultipart = typeof FormData !== 'undefined' && rest.body instanceof FormData;

  const finalHeaders: Record<string, string> = {
    ...(isMultipart ? {} : { 'Content-Type': 'application/json' }),
    'X-Request-Id': requestId,
    ...(headers as Record<string, string> | undefined),
  };
  if (token) finalHeaders['Authorization'] = `Bearer ${token}`;
  // Sprint 6b — CSRF token for the cookie-authed endpoints. Reading the
  // XSRF-TOKEN cookie + echoing in X-XSRF-TOKEN proves to the backend
  // that this request originated from a script that can read the SPA's
  // cookies (i.e. same-origin), not from a cross-site attacker.
  if (methodNeedsCsrf(rest.method)) {
    const csrf = readCsrfToken();
    if (csrf) finalHeaders[CSRF_HEADER_NAME] = csrf;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...rest,
    headers: finalHeaders,
    // Sprint 6 phase-1: opt every cross-origin request into sending +
    // receiving HttpOnly cookies. The backend's CORS config has
    // allowCredentials=true; this is the matching opt-in on the client.
    // No-op while auth.cookie-mode=false on the backend (no cookie set,
    // no cookie sent), so this commit is safe without the flag flip.
    credentials: 'include',
    signal: rest.signal ?? AbortSignal.timeout(REQUEST_TIMEOUT_MS),
  });

  // 401 retry path — only once, only when we have a refresh token.
  if (response.status === 401 && !skipAuth && !_retried) {
    const newAccess = await refreshAccessToken();
    if (newAccess) {
      return apiFetch(endpoint, { ...options, _retried: true });
    }
    // Refresh failed → user is genuinely unauthenticated.
    hardLogout();
  }

  return response;
}

// ── ProblemDetail extraction (Sprint 117 — 117.1) ───────────────────────────
//
// Backend's GlobalExceptionHandler emits RFC-7807 ProblemDetail bodies. We map
// them into a structured, human-readable message so form-submit call sites can
// show the specific reason ("Phone number should be valid") instead of a generic
// "Operation failed" toast.
//
// The EXACT shapes the backend sends (see GlobalExceptionHandler):
//   • Bean-validation (MethodArgumentNotValidException) → 400
//       { type, title, status:400, detail:"Request validation failed",
//         instance, errors: { "<field>": "<message>", ... }, traceId, timestamp }
//     NOTE: `errors` is a field→message MAP (object), not an array.
//   • Business-rule throws (IllegalArgument/Validation/IllegalState/Duplicate/
//     ResourceNotFound) → 4xx with `detail` carrying the actionable message.
//   • Catch-all → 500 with the generic detail "An unexpected error occurred."
//
// Extraction rules:
//   • Only surface 4xx ProblemDetail content. For 5xx we deliberately ignore
//     the body's `detail` and return a safe generic message — a 500 body must
//     never reach the user as if it were actionable (defence-in-depth even
//     though the backend already redacts; a misbehaving proxy/edge could inject
//     a different 5xx body).
//   • Message priority (4xx): detail → first field-error message → title →
//     HTTP status text.

export interface ProblemInfo {
  status: number;
  detail?: string;
  /** Flattened field→message map from the RFC-7807 `errors` extension. */
  fieldErrors?: Record<string, string>;
}

const GENERIC_5XX_MESSAGE = 'Something went wrong on our end. Please try again.';

/** Pull a field→message map out of whatever shape the body's field collection took. */
function extractFieldErrors(body: any): Record<string, string> | undefined {
  if (!body || typeof body !== 'object') return undefined;
  // Backend's canonical shape: `errors` is an object map { field: message }.
  const candidate = body.errors ?? body.fieldErrors ?? body.violations ?? body['invalid-params'];
  if (!candidate) return undefined;
  // Object map (our backend).
  if (!Array.isArray(candidate) && typeof candidate === 'object') {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(candidate)) {
      if (typeof v === 'string') out[k] = v;
    }
    return Object.keys(out).length ? out : undefined;
  }
  // Array shape ({ field, message } | { name, reason }) — tolerated for
  // robustness even though the backend doesn't currently emit it.
  if (Array.isArray(candidate)) {
    const out: Record<string, string> = {};
    for (const e of candidate) {
      if (e && typeof e === 'object') {
        const field = e.field ?? e.name ?? e.property;
        const msg = e.message ?? e.reason ?? e.detail;
        if (typeof field === 'string' && typeof msg === 'string') out[field] = msg;
      }
    }
    return Object.keys(out).length ? out : undefined;
  }
  return undefined;
}

/**
 * Build the best human-readable message + structured ProblemInfo from a failed
 * response's status and (already-parsed, possibly empty) JSON body. Exported for
 * unit testing.
 */
export function parseProblemDetail(
  status: number,
  body: any,
): { message: string; problem: ProblemInfo } {
  const isJsonObject = body && typeof body === 'object';
  const fieldErrors = isJsonObject ? extractFieldErrors(body) : undefined;

  // 5xx: never surface the body — return a safe generic message and carry no
  // detail forward so call sites can't accidentally toast a server internal.
  if (status >= 500) {
    return {
      message: GENERIC_5XX_MESSAGE,
      problem: { status },
    };
  }

  const detail = isJsonObject && typeof body.detail === 'string' ? body.detail : undefined;
  const title = isJsonObject && typeof body.title === 'string' ? body.title : undefined;
  // Older custom-shape responses used `message`.
  const legacyMessage = isJsonObject && typeof body.message === 'string' ? body.message : undefined;
  const firstFieldMessage = fieldErrors ? Object.values(fieldErrors)[0] : undefined;

  const message =
    detail ||
    firstFieldMessage ||
    legacyMessage ||
    title ||
    `HTTP ${status}`;

  return {
    message,
    problem: { status, detail, fieldErrors },
  };
}

/** Error thrown by {@link apiJson} on a non-2xx response. */
export interface ApiError extends Error {
  problem?: ProblemInfo;
}

/**
 * Convenience wrapper for the common case: parse JSON, throw on !ok.
 * Use this from apiService methods. If you need to inspect headers /
 * status, call {@link apiFetch} directly.
 *
 * On failure throws an {@link ApiError} whose `message` is the best
 * human-readable string (RFC-7807 `detail` → first field error → title →
 * status) and whose `problem` carries the structured `{ status, detail,
 * fieldErrors }` so callers can do inline field mapping. Existing
 * `catch (err) { ...err.message }` sites improve automatically.
 */
export async function apiJson<T>(
  endpoint: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const response = await apiFetch(endpoint, options);
  if (!response.ok) {
    // Tolerate non-JSON / empty bodies: parseProblemDetail falls back to the
    // HTTP status text when the body carries nothing usable.
    const body = await response.json().catch(() => null);
    const { message, problem } = parseProblemDetail(response.status, body);
    // Surface the X-Request-Id so the user can quote it in support tickets.
    const requestId = response.headers.get('X-Request-Id');
    const tail = requestId ? ` (id: ${requestId})` : '';
    const error = new Error(message + tail) as ApiError;
    error.problem = problem;
    throw error;
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}
